import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Switch,
  TreeView,
  TreeItem,
  Paper,
  Divider,
  Alert,
  Autocomplete,
  Slider,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Folder,
  FolderOpen,
  SmartToy,
  CheckCircle,
  Cancel,
  PlayArrow,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

// Available context tags.
// A voicetrack is spoken BETWEEN two songs: {previous_*} is the song that has just
// finished and {artist}/{title} (same as {next_*}) is the song the link leads into.
const AVAILABLE_TAGS = [
  { value: 'artist', label: 'Incoming Artist', description: 'Artist of the song this link leads into (same as next_artist)' },
  { value: 'title', label: 'Incoming Title', description: 'Title of the song this link leads into (same as next_title)' },
  { value: 'album', label: 'Incoming Album', description: 'Album of the incoming song' },
  { value: 'genre', label: 'Incoming Genre', description: 'Genre of the incoming song' },
  { value: 'previous_artist', label: 'Previous Artist', description: 'Artist of the song that has just finished' },
  { value: 'previous_title', label: 'Previous Title', description: 'Title of the song that has just finished' },
  { value: 'previous_album', label: 'Previous Album', description: 'Album of the song that has just finished' },
  { value: 'previous_genre', label: 'Previous Genre', description: 'Genre of the song that has just finished' },
  { value: 'prev_2_artist', label: 'Prev 2 Artist', description: 'Artist 2 songs ago' },
  { value: 'prev_2_title', label: 'Prev 2 Title', description: 'Title 2 songs ago' },
  { value: 'prev_3_artist', label: 'Prev 3 Artist', description: 'Artist 3 songs ago' },
  { value: 'prev_3_title', label: 'Prev 3 Title', description: 'Title 3 songs ago' },
  { value: 'prev_4_artist', label: 'Prev 4 Artist', description: 'Artist 4 songs ago' },
  { value: 'prev_4_title', label: 'Prev 4 Title', description: 'Title 4 songs ago' },
  { value: 'prev_5_artist', label: 'Prev 5 Artist', description: 'Artist 5 songs ago' },
  { value: 'prev_5_title', label: 'Prev 5 Title', description: 'Title 5 songs ago' },
  { value: 'next_artist', label: 'Next Artist', description: 'Artist of the incoming song (same as artist)' },
  { value: 'next_title', label: 'Next Title', description: 'Title of the incoming song (same as title)' },
  { value: 'next_album', label: 'Next Album', description: 'Album of the incoming song' },
  { value: 'next_genre', label: 'Next Genre', description: 'Genre of the incoming song' },
  { value: 'next_2_artist', label: 'Next 2 Artist', description: 'Artist 2 songs ahead' },
  { value: 'next_2_title', label: 'Next 2 Title', description: 'Title 2 songs ahead' },
  { value: 'next_3_artist', label: 'Next 3 Artist', description: 'Artist 3 songs ahead' },
  { value: 'next_3_title', label: 'Next 3 Title', description: 'Title 3 songs ahead' },
  { value: 'next_4_artist', label: 'Next 4 Artist', description: 'Artist 4 songs ahead' },
  { value: 'next_4_title', label: 'Next 4 Title', description: 'Title 4 songs ahead' },
  { value: 'next_5_artist', label: 'Next 5 Artist', description: 'Artist 5 songs ahead' },
  { value: 'next_5_title', label: 'Next 5 Title', description: 'Title 5 songs ahead' },
  { value: 'time_spoken', label: 'Time (spoken)', description: 'Air time in words, e.g. "twenty past three" (station time)' },
  { value: 'part_of_day', label: 'Part of Day', description: 'morning / afternoon / evening / night' },
  { value: 'day_name', label: 'Day Name', description: 'e.g. Thursday' },
  { value: 'date_spoken', label: 'Date (spoken)', description: 'e.g. "Thursday the tenth of September"' },
  { value: 'hour_spoken', label: 'Hour (spoken)', description: 'e.g. "three o\'clock"' },
  { value: 'month_name', label: 'Month Name', description: 'e.g. September' },
  { value: 'time_12h', label: 'Time (12h)', description: 'e.g. 3:20 PM' },
  { value: 'time_24h', label: 'Time (24h)', description: 'e.g. 15:20' },
  { value: 'hour', label: 'Hour (number)', description: 'Air-time hour as a number (0-23)' },
  { value: 'day_of_week', label: 'Day of Week (number)', description: 'Day of week as a number (0-6, Sunday = 0)' },
  { value: 'date', label: 'Date (numeric)', description: 'Air date in numeric form' },
  { value: 'year', label: 'Year', description: 'e.g. 2026' },
];

const TAG_BY_VALUE = Object.fromEntries(AVAILABLE_TAGS.map((tag) => [tag.value, tag]));

const PRIMARY_TAG_GROUPS = [
  { label: 'Coming up', values: ['artist', 'title', 'album', 'genre'] },
  { label: 'Just played', values: ['previous_artist', 'previous_title'] },
  { label: 'Time (spoken)', values: ['time_spoken', 'part_of_day', 'day_name', 'date_spoken'] },
];

const EXTENDED_TAG_GROUPS = [
  { label: 'Either side', values: ['previous_album', 'previous_genre', 'next_artist', 'next_title', 'next_album', 'next_genre'] },
  { label: '2–5 back', values: ['prev_2_artist', 'prev_2_title', 'prev_3_artist', 'prev_3_title', 'prev_4_artist', 'prev_4_title', 'prev_5_artist', 'prev_5_title'] },
  { label: '2–5 ahead', values: ['next_2_artist', 'next_2_title', 'next_3_artist', 'next_3_title', 'next_4_artist', 'next_4_title', 'next_5_artist', 'next_5_title'] },
  { label: 'Other time', values: ['hour_spoken', 'month_name', 'time_12h', 'time_24h', 'hour', 'day_of_week', 'date', 'year'] },
];

export default function PromptGenerator({ stationId, voices }) {
  const [folders, setFolders] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showExtendedTags, setShowExtendedTags] = useState(false);
  const templateInputRef = useRef(null);
  const templateSelectionRef = useRef({ start: null, end: null });
  const pendingCaretRef = useRef(null);

  const resetTagUiState = () => {
    setShowExtendedTags(false);
    templateSelectionRef.current = { start: null, end: null };
    pendingCaretRef.current = null;
  };

  // Test generation state
  const [testGenerating, setTestGenerating] = useState(false);
  const [testPromptId, setTestPromptId] = useState(null);
  const [testScript, setTestScript] = useState('');
  const [testWarnings, setTestWarnings] = useState([]);
  const [testLength, setTestLength] = useState(null);
  const [testAudio, setTestAudio] = useState(null);
  const [testProgress, setTestProgress] = useState('');

  // Prompt form state
  const [promptForm, setPromptForm] = useState({
    name: '',
    description: '',
    folder_id: null,
    voice_id: null,
    prompt_template: '',
    max_length: 15,
    tone: 'casual',
    include_music_info: true,
    include_time_reference: false,
    language_code: 'en',
    tags: [],
    requires_previous: false,
    requires_next: false,
    requires_current: false,
    rss_feed_ids: [],
    promo_id: null,
    settings: {},
  });

  // Music bed categories state
  const [musicBedCategories, setMusicBedCategories] = useState([]);

  // RSS feeds state
  const [rssFeeds, setRssFeeds] = useState([]);

  // Promos state
  const [promos, setPromos] = useState([]);

  // Folder form state
  const [folderForm, setFolderForm] = useState({
    name: '',
    parent_id: null,
    description: '',
    color: '#1976D2',
  });

  useEffect(() => {
    fetchFolders();
    fetchPrompts();
    fetchRssFeeds();
    fetchPromos();
    fetchMusicBedCategories();
  }, [stationId]);

  useLayoutEffect(() => {
    if (pendingCaretRef.current == null) return;
    const input = templateInputRef.current;
    const caret = pendingCaretRef.current;
    pendingCaretRef.current = null;
    if (!input) return;
    input.focus();
    input.setSelectionRange(caret, caret);
  }, [promptForm.prompt_template]);

  const fetchPromos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/promos`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setPromos(res.data || []);
    } catch (err) {
      console.error('Error fetching promos:', err);
    }
  };

  const fetchMusicBedCategories = async () => {
    try {
      const stationParams = stationId ? { station_id: stationId } : {};
      const res = await axios.get(`${API_URL}/api/categories`, {
        params: stationParams,
        withCredentials: true
      });
      const allCats = res.data || [];
      const mbCats = allCats.filter(c => c.category_type === 'music_bed' || c.name === 'Music Beds');
      const parentMB = allCats.find(c => c.name === 'Music Beds' || c.category_type === 'music_bed');
      if (parentMB) {
        try {
          const subRes = await axios.get(`${API_URL}/api/categories/${parentMB.id}/subcategories`, {
            params: stationParams,
            withCredentials: true
          });
          const subCats = subRes.data || [];
          setMusicBedCategories([...mbCats, ...subCats.filter(sc => !mbCats.find(mc => mc.id === sc.id))]);
        } catch {
          setMusicBedCategories(mbCats);
        }
      } else {
        setMusicBedCategories(mbCats);
      }
    } catch (err) {
      console.error('Error fetching music bed categories:', err);
    }
  };

  const fetchRssFeeds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/rss-feeds`, {
        params: { stationId: stationId },
        withCredentials: true,
      });
      setRssFeeds(res.data || []);
    } catch (err) {
      console.error('Error fetching RSS feeds:', err);
      // If endpoint doesn't exist yet, silently fail
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices/prompts/folders`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setFolders(res.data || []);
    } catch (err) {
      console.error('Error fetching folders:', err);
    }
  };

  const fetchPrompts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices/prompts`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setPrompts(res.data || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  };

  const handleCreateFolder = async () => {
    if (!folderForm.name.trim()) {
      setError('Folder name is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/ai/voices/prompts/folders`,
        { ...folderForm, station_id: stationId },
        { withCredentials: true }
      );
      setSuccess('Folder created successfully');
      setFolderDialogOpen(false);
      setFolderForm({ name: '', parent_id: null, description: '', color: '#1976D2' });
      fetchFolders();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = async () => {
    if (!promptForm.name.trim()) {
      setError('Prompt name is required');
      return;
    }
    if (!promptForm.prompt_template.trim()) {
      setError('Prompt template is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const url = editingPrompt
        ? `${API_URL}/api/ai/voices/prompts/${editingPrompt.id}`
        : `${API_URL}/api/ai/voices/prompts`;
      const method = editingPrompt ? 'put' : 'post';

      await axios[method](
        url,
        { ...promptForm, station_id: stationId },
        { withCredentials: true }
      );

      setSuccess(editingPrompt ? 'Prompt updated successfully' : 'Prompt created successfully');
      setDialogOpen(false);
      setEditingPrompt(null);
      resetTagUiState();
      setPromptForm({
        name: '',
        description: '',
        folder_id: null,
        voice_id: null,
        prompt_template: '',
        max_length: 15,
        tone: 'casual',
        include_music_info: true,
        include_time_reference: false,
        language_code: 'en',
        tags: [],
        requires_previous: false,
        requires_next: false,
        requires_current: false,
        rss_feed_ids: [],
        promo_id: null,
        settings: {},
      });
      fetchPrompts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    const s = prompt.settings || {};
    setPromptForm({
      name: prompt.name || '',
      description: prompt.description || '',
      folder_id: prompt.folder_id || null,
      voice_id: prompt.voice_id || null,
      prompt_template: prompt.prompt_template || '',
      max_length: prompt.max_length || 15,
      tone: prompt.tone || 'casual',
      include_music_info: prompt.include_music_info !== false,
      include_time_reference: prompt.include_time_reference === true,
      language_code: prompt.language_code || 'en',
      tags: Array.isArray(prompt.tags) ? prompt.tags : [],
      requires_previous: prompt.requires_previous === true,
      requires_next: prompt.requires_next === true,
      requires_current: prompt.requires_current === true,
      rss_feed_ids: prompt.rss_feed_ids || [],
      promo_id: prompt.promo_id || null,
      settings: {
        sweep_over_intro: s.sweep_over_intro || false,
        music_bed_enabled: s.music_bed_enabled || false,
        music_bed_category_id: s.music_bed_category_id || null,
        music_bed_volume_db: s.music_bed_volume_db != null ? s.music_bed_volume_db : -12,
        max_sources_per_link: s.max_sources_per_link != null ? s.max_sources_per_link : 5,
      },
    });
    resetTagUiState();
    setDialogOpen(true);
  };

  const handleDeletePrompt = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;

    try {
      await axios.delete(`${API_URL}/api/ai/voices/prompts/${promptId}`, {
        withCredentials: true,
      });
      setSuccess('Prompt deleted successfully');
      fetchPrompts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete prompt');
    }
  };

  const handleGenerateTest = async (prompt) => {
    if (!prompt) return;

    const voiceId = prompt.voice_id || (voices && voices.length > 0 ? voices[0].id : null);
    if (!voiceId) {
      setError('No voice available. Please add a voice in AI Content → My Voices, or assign a voice to this prompt.');
      return;
    }

    setTestGenerating(true);
    setTestPromptId(prompt.id);
    setTestScript('');
    setTestWarnings([]);
    setTestLength(null);
    setTestAudio(null);
    setTestProgress('Generating script...');
    setError('');
    setSuccess('');

    try {
      // Mirror what the on-air generator supplies: the link sits between the song that has
      // just finished (previous_*) and the song it leads into (next_* — also exposed as
      // {artist}/{title}). Time tags are filled in by the server in station time.
      const scriptRes = await axios.post(
        `${API_URL}/api/ai/generate-script-from-prompt`,
        {
          prompt_id: prompt.id,
          voice_id: voiceId,
          context: {
            artist: 'Dua Lipa',
            title: 'Levitating',
            album: 'Future Nostalgia',
            genre: 'Pop',
            previous_artist: 'Coldplay',
            previous_title: 'Yellow',
            previous_album: 'Parachutes',
            previous_genre: 'Alternative',
            prev_2_artist: 'Adele',
            prev_2_title: 'Hello',
            prev_3_artist: 'Ed Sheeran',
            prev_3_title: 'Shivers',
            prev_4_artist: 'Harry Styles',
            prev_4_title: 'As It Was',
            prev_5_artist: 'Lizzo',
            prev_5_title: 'About Damn Time',
            next_artist: 'Dua Lipa',
            next_title: 'Levitating',
            next_album: 'Future Nostalgia',
            next_genre: 'Pop',
            next_2_artist: 'The Weeknd',
            next_2_title: 'Blinding Lights',
            next_3_artist: 'Billie Eilish',
            next_3_title: 'Happier Than Ever',
            next_4_artist: 'Sam Fender',
            next_4_title: 'Seventeen Going Under',
            next_5_artist: 'Olivia Rodrigo',
            next_5_title: 'good 4 u',
          },
        },
        { withCredentials: true }
      );

      const script = scriptRes.data?.script;
      if (!script) throw new Error('Failed to generate script');

      // Server warnings already cover banned words that survived a rewrite, tags
      // with no value, and a script that runs past its slot.
      const warnings = Array.isArray(scriptRes.data?.warnings) ? [...scriptRes.data.warnings] : [];
      setTestScript(script);
      setTestLength({
        seconds: Number(scriptRes.data?.estimated_seconds) || null,
        overrun: scriptRes.data?.overrun === true,
        slot: prompt.max_length || 15,
      });
      setTestProgress('Generating audio...');

      const audioRes = await axios.post(
        `${API_URL}/api/ai/voices/generate`,
        { voice_id: voiceId, text: script },
        { withCredentials: true, responseType: 'blob' }
      );

      try {
        const ttsWarnings = JSON.parse(decodeURIComponent(audioRes.headers?.['x-script-warnings'] || '[]'));
        if (Array.isArray(ttsWarnings)) warnings.push(...ttsWarnings);
      } catch (_) {
        // header absent or malformed — nothing to show
      }
      setTestWarnings(warnings);

      const audioUrl = URL.createObjectURL(audioRes.data);
      setTestAudio(audioUrl);
      setTestProgress('');
      setSuccess(warnings.length > 0 ? 'Test generated with warnings — check the script below.' : 'Test generated successfully!');

      const audio = new Audio(audioUrl);
      audio.play();
    } catch (err) {
      console.error('Error generating test:', err);
      setError(err.response?.data?.message || 'Failed to generate test. Check your AI configuration.');
      setTestProgress('');
    } finally {
      setTestGenerating(false);
    }
  };

  const rememberTemplateSelection = (event) => {
    const input = event?.target;
    if (!input || typeof input.selectionStart !== 'number') return;
    templateSelectionRef.current = {
      start: input.selectionStart,
      end: input.selectionEnd,
    };
  };

  const insertTag = (tag) => {
    const tagPlaceholder = `{${tag}}`;
    setPromptForm((prev) => {
      const template = prev.prompt_template || '';
      const start = Math.min(templateSelectionRef.current.start ?? template.length, template.length);
      const end = Math.min(templateSelectionRef.current.end ?? template.length, template.length);
      const next = `${template.slice(0, start)}${tagPlaceholder}${template.slice(end)}`;
      const caret = start + tagPlaceholder.length;
      templateSelectionRef.current = { start: caret, end: caret };
      pendingCaretRef.current = caret;
      return {
        ...prev,
        prompt_template: next,
      };
    });
  };

  const renderTagChips = (values) => (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {values.map((value) => {
        const tag = TAG_BY_VALUE[value];
        if (!tag) return null;
        return (
          <Tooltip key={tag.value} title={tag.description} arrow>
            <Chip
              size="small"
              label={`{${tag.value}}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertTag(tag.value)}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#22d3ee30' } }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );

  const filteredPrompts = selectedFolder
    ? prompts.filter((p) => p.folder_id === selectedFolder)
    : prompts.filter((p) => !p.folder_id);

  const buildFolderTree = (parentId = null) => {
    return folders
      .filter((f) => f.parent_id === parentId)
      .map((folder) => ({
        ...folder,
        children: buildFolderTree(folder.id),
      }));
  };

  const folderTree = buildFolderTree();

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Folder Tree Sidebar */}
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                  Folders
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setFolderDialogOpen(true)}
                  sx={{ color: '#22d3ee' }}
                >
                  New Folder
                </Button>
              </Box>

              <List>
                <ListItem
                  button
                  selected={selectedFolder === null}
                  onClick={() => setSelectedFolder(null)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': { bgcolor: '#22d3ee20' },
                  }}
                >
                  <ListItemText primary="All Prompts" />
                </ListItem>
                {folders.map((folder) => (
                  <ListItem
                    key={folder.id}
                    button
                    selected={selectedFolder === folder.id}
                    onClick={() => setSelectedFolder(folder.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': { bgcolor: '#22d3ee20' },
                    }}
                  >
                    <Folder sx={{ mr: 1, color: folder.color || '#1976D2' }} />
                    <ListItemText primary={folder.name} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Prompts List */}
        <Grid item xs={12} md={9}>
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                  Prompts {selectedFolder && `(${filteredPrompts.length})`}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditingPrompt(null);
                    setPromptForm({
                      name: '',
                      description: '',
                      folder_id: selectedFolder,
                      voice_id: null,
                      prompt_template: '',
                      max_length: 15,
                      tone: 'casual',
                      include_music_info: true,
                      include_time_reference: false,
                      language_code: 'en',
                      tags: [],
                      requires_previous: false,
                      requires_next: false,
                      requires_current: false,
                      rss_feed_ids: [],
                      promo_id: null,
                      settings: {},
                    });
                    resetTagUiState();
                    setDialogOpen(true);
                  }}
                  sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#0891b2' } }}
                >
                  New Prompt
                </Button>
              </Box>

              {filteredPrompts.length === 0 ? (
                <Alert severity="info">No prompts in this folder. Create one to get started.</Alert>
              ) : (
                <List>
                  {filteredPrompts.map((prompt) => (
                    <React.Fragment key={prompt.id}>
                      <ListItem
                        sx={{
                          bgcolor: 'var(--rs-bg)',
                          borderRadius: 1,
                          mb: 1,
                          border: testPromptId === prompt.id && testAudio ? '1px solid #4caf50' : '1px solid var(--rs-border)',
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={`#${prompt.id}`}
                                size="small"
                                sx={{
                                  bgcolor: '#22d3ee20',
                                  color: '#22d3ee',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  height: 22,
                                }}
                              />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {prompt.name}
                              </Typography>
                              {prompt.folder_name && (
                                <Chip
                                  label={prompt.folder_name}
                                  size="small"
                                  sx={{
                                    bgcolor: prompt.folder_color || '#1976D2',
                                    color: 'var(--rs-text)',
                                    fontSize: '0.7rem',
                                  }}
                                />
                              )}
                              {prompt.voice_id && (
                                <Chip
                                  label={`Voice: ${voices.find(v => v.id === prompt.voice_id)?.name || `#${prompt.voice_id}`}`}
                                  size="small"
                                  sx={{ bgcolor: '#9c27b020', color: '#d8b4fe', fontSize: '0.65rem', height: 20 }}
                                />
                              )}
                              {prompt.promo_name && (
                                <Chip
                                  label={`Promo: ${prompt.promo_name}`}
                                  size="small"
                                  sx={{ bgcolor: '#FF980020', color: '#FFB74D', fontSize: '0.65rem', height: 20 }}
                                />
                              )}
                              {prompt.settings?.sweep_over_intro && (
                                <Chip label="Sweep" size="small" sx={{ bgcolor: '#4caf5020', color: '#81c784', fontSize: '0.65rem', height: 20 }} />
                              )}
                              {prompt.settings?.music_bed_enabled && (
                                <Chip label="Music Bed" size="small" sx={{ bgcolor: '#ff980020', color: '#ffb74d', fontSize: '0.65rem', height: 20 }} />
                              )}
                              {prompt.requires_previous && (
                                <Chip label="Requires Previous" size="small" sx={{ fontSize: '0.7rem' }} />
                              )}
                              {prompt.requires_next && (
                                <Chip label="Requires Next" size="small" sx={{ fontSize: '0.7rem' }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" sx={{ color: '#999', mb: 0.5 }}>
                                {prompt.prompt_template}
                              </Typography>
                              {prompt.tags && prompt.tags.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {prompt.tags.map((tag) => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      sx={{ fontSize: '0.65rem', height: 20 }}
                                    />
                                  ))}
                                </Box>
                              )}
                              {/* Test results inline */}
                              {testPromptId === prompt.id && testScript && (
                                <Paper sx={{ p: 1, mt: 1, bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                                  <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                                    Test Script:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#ccc', fontSize: '0.75rem' }}>
                                    {testScript}
                                  </Typography>
                                  {testLength?.seconds != null && (
                                    <Typography
                                      variant="caption"
                                      sx={{ display: 'block', mt: 0.5, color: testLength.overrun ? '#ffb74d' : '#8a8a8a' }}
                                    >
                                      Reads in about {Math.round(testLength.seconds)}s
                                      {testLength.overrun
                                        ? ` — over the ${testLength.slot}s slot`
                                        : ` of the ${testLength.slot}s slot`}
                                    </Typography>
                                  )}
                                  {testWarnings.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      {testWarnings.map((warning, idx) => (
                                        <Typography key={idx} variant="caption" sx={{ color: '#ffb74d', display: 'block' }}>
                                          Warning: {warning}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                  {testAudio && (
                                    <Box sx={{ mt: 1 }}>
                                      <audio controls src={testAudio} style={{ width: '100%', height: 32 }} />
                                    </Box>
                                  )}
                                </Paper>
                              )}
                              {testPromptId === prompt.id && testProgress && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                  <CircularProgress size={16} sx={{ color: '#22d3ee' }} />
                                  <Typography variant="caption" sx={{ color: '#22d3ee' }}>{testProgress}</Typography>
                                </Box>
                              )}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleGenerateTest(prompt)}
                            disabled={testGenerating}
                            sx={{ color: '#4caf50', mr: 1 }}
                            title="Generate Test - hear this prompt"
                          >
                            <PlayArrow />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleEditPrompt(prompt)}
                            sx={{ color: '#22d3ee', mr: 1 }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeletePrompt(prompt.id)}
                            sx={{ color: '#ff6b6b' }}
                          >
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Prompt Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingPrompt(null);
          resetTagUiState();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee' }}>
          {editingPrompt ? 'Edit Prompt' : 'Create Prompt'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prompt Name"
                value={promptForm.name}
                onChange={(e) => setPromptForm({ ...promptForm, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={promptForm.description}
                onChange={(e) => setPromptForm({ ...promptForm, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Folder</InputLabel>
                <Select
                  value={promptForm.folder_id || ''}
                  onChange={(e) => setPromptForm({ ...promptForm, folder_id: e.target.value || null })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="">None</MenuItem>
                  {folders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Voice</InputLabel>
                <Select
                  value={promptForm.voice_id || ''}
                  onChange={(e) => setPromptForm({ ...promptForm, voice_id: e.target.value || null })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="">Default</MenuItem>
                  {voices.map((voice) => (
                    <MenuItem key={voice.id} value={voice.id}>
                      {voice.name} ({voice.voice_type === 'speechify' ? 'Speechify' : (voice.voice_type === 'elevenlabs' ? 'ElevenLabs' : 'Cloned')})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Linked Promo (Optional)</InputLabel>
                <Select
                  value={promptForm.promo_id || ''}
                  onChange={(e) => setPromptForm({ ...promptForm, promo_id: e.target.value || null })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="">None</MenuItem>
                  {promos.filter(p => p.is_active !== false).map((promo) => (
                    <MenuItem key={promo.id} value={promo.id}>
                      {promo.name}{promo.category_name ? ` (${promo.category_name})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                When linked, the promo script will be used when this prompt fires during playout
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Prompt Template"
                value={promptForm.prompt_template}
                onChange={(e) => {
                  setPromptForm({ ...promptForm, prompt_template: e.target.value });
                  rememberTemplateSelection(e);
                }}
                onSelect={rememberTemplateSelection}
                onClick={rememberTemplateSelection}
                onKeyUp={rememberTemplateSelection}
                inputRef={templateInputRef}
                placeholder="That was {previous_artist} with '{previous_title}'. Up next, {artist} and '{title}'."
                required
              />
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'rgba(34, 211, 238, 0.06)', border: '1px solid var(--rs-border)' }}>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                  Click a tag to insert it at the cursor. The link is spoken between two songs: "Just played" is the
                  song that has just finished and "Coming up" ({'{artist}'} / {'{title}'}) is the song the link leads into.
                  Spoken time tags are filled in station time when the link is generated.
                </Typography>
                {PRIMARY_TAG_GROUPS.map((group) => (
                  <Box key={group.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: '#22d3ee', minWidth: 96, pt: 0.5, fontWeight: 600 }}>
                      {group.label}
                    </Typography>
                    {renderTagChips(group.values)}
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() => setShowExtendedTags((open) => !open)}
                  startIcon={showExtendedTags ? <ExpandLess /> : <ExpandMore />}
                  sx={{ color: '#22d3ee', textTransform: 'none', px: 0, minWidth: 0 }}
                >
                  {showExtendedTags ? 'Hide extra tags' : 'Show more tags (album/genre, 2–5 tracks back / ahead, other time formats)'}
                </Button>
                <Collapse in={showExtendedTags}>
                  <Box sx={{ mt: 1 }}>
                    {EXTENDED_TAG_GROUPS.map((group) => (
                      <Box key={group.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: '#22d3ee', minWidth: 96, pt: 0.5, fontWeight: 600 }}>
                          {group.label}
                        </Typography>
                        {renderTagChips(group.values)}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Duration (seconds)"
                value={promptForm.max_length}
                onChange={(e) => setPromptForm({ ...promptForm, max_length: parseInt(e.target.value) || 15 })}
                inputProps={{ min: 5, max: 300 }}
                helperText="Spoken length the script should be written for. 15s is a short ad-lib; 60–120s is a longer promo."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Tone</InputLabel>
                <Select
                  value={promptForm.tone}
                  onChange={(e) => setPromptForm({ ...promptForm, tone: e.target.value })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="casual">Casual</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="energetic">Energetic</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2, bgcolor: 'var(--rs-border)' }} />
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                Context Requirements
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={promptForm.requires_previous}
                    onChange={(e) => setPromptForm({ ...promptForm, requires_previous: e.target.checked })}
                    sx={{ color: '#22d3ee' }}
                  />
                }
                label="Requires Previous Track (won't generate if no previous track)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={promptForm.requires_next}
                    onChange={(e) => setPromptForm({ ...promptForm, requires_next: e.target.checked })}
                    sx={{ color: '#22d3ee' }}
                  />
                }
                label="Requires Next Track (won't generate if no next track)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={promptForm.requires_current}
                    onChange={(e) => setPromptForm({ ...promptForm, requires_current: e.target.checked })}
                    sx={{ color: '#22d3ee' }}
                  />
                }
                label="Requires Incoming Track ({artist}/{title} — same song as Next)"
              />
              <Autocomplete
                multiple
                options={AVAILABLE_TAGS}
                getOptionLabel={(option) => `${option.label} ({${option.value}})`}
                filterOptions={(options, state) => {
                  const query = (state.inputValue || '').toLowerCase();
                  if (!query) return options;
                  return options.filter((option) =>
                    option.value.toLowerCase().includes(query)
                    || option.label.toLowerCase().includes(query)
                  );
                }}
                value={AVAILABLE_TAGS.filter((tag) => (promptForm.tags || []).includes(tag.value))}
                onChange={(e, newValue) => {
                  setPromptForm((prev) => ({
                    ...prev,
                    tags: newValue.map((v) => v.value),
                  }));
                }}
                sx={{ mt: 1.5 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Required tags (optional)"
                    helperText="Skip this prompt at generate time if these playlist values are missing"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.value}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2, bgcolor: 'var(--rs-border)' }} />
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                Content Sources (Optional)
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                Select RSS feeds or news websites to use as context when generating content. The AI will use these sources to create scripts, news, or entertainment content based on the latest articles.
              </Typography>
              <Autocomplete
                multiple
                options={rssFeeds.filter(feed => feed.is_active !== false)}
                getOptionLabel={(option) => option.name || option.url}
                value={rssFeeds.filter((feed) => promptForm.rss_feed_ids.includes(feed.id))}
                onChange={(e, newValue) => {
                  setPromptForm({
                    ...promptForm,
                    rss_feed_ids: newValue.map((v) => v.id),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Content Sources"
                    helperText="Sources to draw from for this prompt. Manage them in the RSS Feeds tab."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name || option.url}
                      size="small"
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.name || 'Unnamed source'}</Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {option.source_type === 'website' ? 'Website · ' : 'RSS · '}{option.url}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max sources per link"
                    value={promptForm.settings?.max_sources_per_link ?? 5}
                    onChange={(e) => setPromptForm({
                      ...promptForm,
                      settings: {
                        ...promptForm.settings,
                        max_sources_per_link: Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 5)),
                      },
                    })}
                    inputProps={{ min: 1, max: 5 }}
                    helperText="How many of the selected sources one link may use (1-5)"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2, bgcolor: 'var(--rs-border)' }} />
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                Playback &amp; Audio Settings
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                These settings control how the generated AI link is played on-air and whether a music bed is mixed underneath.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={promptForm.settings?.sweep_over_intro || false}
                    onChange={(e) => setPromptForm({
                      ...promptForm,
                      settings: { ...promptForm.settings, sweep_over_intro: e.target.checked },
                    })}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' } }}
                  />
                }
                label="Sweep Over Intro"
              />
              <Typography variant="caption" sx={{ color: '#666', display: 'block', ml: 6, mb: 2 }}>
                When enabled, the AI link will play over the intro of the next track, creating a seamless sweep transition with ducking applied.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={promptForm.settings?.music_bed_enabled || false}
                    onChange={(e) => setPromptForm({
                      ...promptForm,
                      settings: { ...promptForm.settings, music_bed_enabled: e.target.checked },
                    })}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' } }}
                  />
                }
                label="Music Bed"
              />
              <Typography variant="caption" sx={{ color: '#666', display: 'block', ml: 6, mb: 1 }}>
                Play a music bed underneath the generated AI link at a lower volume.
              </Typography>

              {promptForm.settings?.music_bed_enabled && (
                <>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#999' }}>Music Bed Category</InputLabel>
                        <Select
                          value={promptForm.settings?.music_bed_category_id || ''}
                          onChange={(e) => setPromptForm({
                            ...promptForm,
                            settings: { ...promptForm.settings, music_bed_category_id: e.target.value || null },
                          })}
                          label="Music Bed Category"
                          sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                        >
                          <MenuItem value="">Select Category</MenuItem>
                          {musicBedCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.parent_category_id ? '↳ ' : ''}{cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Music Bed Volume (dB)"
                        value={promptForm.settings?.music_bed_volume_db != null ? promptForm.settings.music_bed_volume_db : -12}
                        onChange={(e) => setPromptForm({
                          ...promptForm,
                          settings: { ...promptForm.settings, music_bed_volume_db: parseFloat(e.target.value) },
                        })}
                        inputProps={{ min: -40, max: 0, step: 1 }}
                        helperText="Volume for music bed (e.g. -12 dB)"
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--rs-border)' }}>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setEditingPrompt(null);
              resetTagUiState();
            }}
            sx={{ color: '#999' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePrompt}
            disabled={loading || !promptForm.name || !promptForm.prompt_template}
            sx={{ bgcolor: '#22d3ee', color: '#0a0c11', '&:hover': { bgcolor: '#0891b2' } }}
          >
            {loading ? 'Saving...' : editingPrompt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Folder Dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee' }}>Create Folder</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Folder Name"
                value={folderForm.name}
                onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={folderForm.description}
                onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Parent Folder</InputLabel>
                <Select
                  value={folderForm.parent_id || ''}
                  onChange={(e) => setFolderForm({ ...folderForm, parent_id: e.target.value || null })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="">None (Root)</MenuItem>
                  {folders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="color"
                label="Color"
                value={folderForm.color}
                onChange={(e) => setFolderForm({ ...folderForm, color: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--rs-border)' }}>
          <Button onClick={() => setFolderDialogOpen(false)} sx={{ color: '#999' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            disabled={loading || !folderForm.name}
            sx={{ bgcolor: '#22d3ee', color: '#0a0c11', '&:hover': { bgcolor: '#0891b2' } }}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
